<template>
  <div class="modal fade" id="recentSalePriceModal" data-bs-backdrop="static" tabindex="-1" aria-modal="true" role="dialog">
    <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <div>
            <h5 class="modal-title mb-1" id="recentSalePriceModalTitle">{{ title }}</h5>
            <div v-if="modalContractNo">
              Contract No: {{ modalContractNo }}
            </div>
          </div>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div class="table-responsive">
            <table class="table table-hover history-sale-price-table">
              <thead>
                <tr>
                  <th class="date-col">Date</th>
                  <th class="customer-col">Customer</th>
                  <th class="text-end quantity-col">Quantity</th>
                  <th class="text-end price-col">Price (Ex.rate)</th>
                  <th class="tax-col">Tax</th>
                  <!-- <th class="text-end">Ex.rate</th> -->
                  <th class="text-end history-action-col">Action</th>
                </tr>
              </thead>
              <tbody class="table-border-bottom-0">
                <tr v-if="!histories.length">
                  <td colspan="6" class="text-center text-muted py-3">No recent sale price data.</td>
                </tr>
                <tr
                  v-else
                  v-for="(history, historyIndex) in histories"
                  :key="history.contractId || history.contractNo || historyIndex"
                  :class="{ 'history-row-selected': selectedHistoryIndex === historyIndex }"
                  @mouseenter="$emit('hover-history', historyIndex)"
                  @mouseleave="$emit('clear-hover-history')"
                >
                  <td class="date-col">{{ formatHistoryDate(history.date) }}</td>
                  <td class="customer-col">
                    <span class="customer-name-truncate"
                      v-tooltip="history.customer_name"
                    >
                      {{ history.customer_name }}
                    </span>
                  </td>
                  <td class="text-end quantity-col">{{ history.quantity }}</td>
                  <td class="text-end price-col">
                    <span class="me-1">{{ formatHistoryPrice(history.price, history.sell_currency) }}</span>
                    <span>({{ formatExchangeRate(history.rate_ex) }})</span>
                  </td>
                  <td class="tax-col">
                    <span class="tax-truncate" v-tooltip="formatSaleTaxes(history)">
                      {{ formatSaleTaxes(history) }}
                    </span>
                  </td>
                  <!-- <td class="text-end">{{ formatExchangeRate(history.rate_ex) }}</td> -->
                  <td class="text-end history-action-col">
                    <button
                      type="button"
                      class="btn btn-sm btn-primary history-select-btn"
                      :class="{
                        'is-visible': hoveredHistoryIndex === historyIndex || selectedHistoryIndex === historyIndex,
                        'is-selected': selectedHistoryIndex === historyIndex
                      }"
                      @click="$emit('select-history', { history, historyIndex })"
                    >
                      <template v-if="selectedHistoryIndex === historyIndex">
                        <i class="bx bx-check"></i>
                      </template>
                      <template v-else>
                        Select
                      </template>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="modal-footer"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { utilities } from "../../services/utilities.js";

export default {
  name: "RecentSalePriceModal",
  props: {
    title: {
      type: String,
      default: "Recent Sale Price"
    },
    histories: {
      type: Array,
      default: () => []
    },
    hoveredHistoryIndex: {
      type: Number,
      default: null
    },
    selectedHistoryIndex: {
      type: Number,
      default: null
    }
  },
  computed: {
    selectedHistory() {
      if (
        !Number.isInteger(this.selectedHistoryIndex) ||
        this.selectedHistoryIndex < 0 ||
        this.selectedHistoryIndex >= this.histories.length
      ) {
        return null;
      }
      return this.histories[this.selectedHistoryIndex];
    },
    modalContractNo() {
      return this.selectedHistory && this.selectedHistory.contractNo
        ? this.selectedHistory.contractNo
        : "";
    }
  },
  methods: {
    formatHistoryDate(dateValue) {
      if (!dateValue) return "";
      return utilities.changeFormatDate(dateValue);
    },
    formatSaleTaxes(history) {
      const sellTaxPairs = [1, 2, 3]
        .map((index) => {
          const name = (history && history[`tax_sell_name_${index}`]) || "";
          const value = history && history[`tax_sell_value_${index}`];
          if (!name || value == null || value === "") return null;
          return `${name} ${Number(value).toLocaleString("en-US")}%`;
        })
        .filter(Boolean);

      return sellTaxPairs.length ? sellTaxPairs.join(" | ") : "-";
    },
    formatHistoryPrice(price, sellCurrency = "") {
      const normalizedPrice = Number(price ?? 0).toLocaleString("en-US");
      return sellCurrency ? `${sellCurrency} ${normalizedPrice}` : normalizedPrice;
    },
    formatExchangeRate(rateEx) {
      if (rateEx == null || rateEx === "") return "";
      return Number(rateEx).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
    }
  }
};
</script>

<style scoped>
  .history-sale-price-table {
    table-layout: fixed;
    width: 100%;
  }
  .history-sale-price-table .date-col {
    width: 150px;
  }
  .history-sale-price-table .customer-col {
    width: 180px;
    max-width: 180px;
  }
  .history-sale-price-table .quantity-col {
    width: 130px;
  }
  .history-sale-price-table .price-col {
    width: 180px;
  }
  .history-sale-price-table .tax-col {
    width: 170px;
    max-width: 170px;
  }
  .history-sale-price-table .history-action-col {
    width: 110px;
    min-width: 110px;
  }
  .history-select-btn {
    visibility: hidden;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.12s ease;
  }
  .history-select-btn.is-visible {
    visibility: visible;
    opacity: 1;
    pointer-events: auto;
  }
  .history-sale-price-table tbody tr.history-row-selected td {
    background-color: var(--bs-primary, #696cff) !important;
    color: #fff;
  }
  .history-sale-price-table tbody tr.history-row-selected td span {
    color: #fff;
  }
  .history-select-btn.is-selected {
    min-width: 34px;
    padding-left: 0.45rem;
    padding-right: 0.45rem;
  }
  .customer-name-truncate {
    display: inline-block;
    max-width: 160px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: bottom;
  }
  .tax-truncate {
    display: inline-block;
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: bottom;
  }
  @media (min-width: 992px) {
    .modal-lg, .modal-xl {
      --bs-modal-width: 60rem;
    }
  }
</style>
